import { computed, inject, Injectable, signal } from '@angular/core';
import { ConfigService } from '@cmi/addin-core';
import { fromOffice } from '../from-office';

/** The most mails filed in one go; a larger selection only gets a message. */
export const MAX_BATCH_SIZE = 20;

/** A mail selected in Outlook's message list, as far as the selection tells. */
export interface SelectedMail {
  itemId: string;
  subject: string;
}

/** What Outlook has selected in the message list. */
export interface Selection {
  mails: SelectedMail[];
  /** The mails are in a mailbox other than the user's own, which batch filing leaves out. */
  sharedMailbox: boolean;
}

const NO_SELECTION: Selection = { mails: [], sharedMailbox: false };

/**
 * The mails selected in Outlook, for filing several at once.
 *
 * With multi-select on, a pinned pane sees the message list rather than one
 * open mail: Outlook raises `SelectedItemsChanged` next to `ItemChanged`, and
 * `mailbox.item` is null while several mails are selected.
 *
 * Reading the selection needs requirement set 1.13. Below it the selection is
 * always empty, and the pane goes by the open mail as before.
 */
@Injectable({
  providedIn: 'root'
})
export class SelectionService {
  private readonly _configService = inject(ConfigService);
  private readonly _selection = signal<Selection>(NO_SELECTION);
  /** Reads one after the other: loading an item to check its mailbox allows one at a time. */
  private _reading: Promise<Selection> = Promise.resolve(NO_SELECTION);

  /** As of the last read; the pane reads again on every change Outlook reports. */
  public readonly selection = this._selection.asReadonly();
  /** Several mails selected: the pane is about the batch, not about one mail. */
  public readonly multiple = computed(() => this._selection().mails.length > 1);
  /**
   * Set while a batch keeps to the mails it was started with, whatever is
   * selected meanwhile; the pane stays on the batch until it lets go. See BatchView.
   */
  public readonly batchHeld = signal<boolean>(false);

  public constructor() {
    if (!this._canReadSelection()) {
      return;
    }

    Office.context.mailbox.addHandlerAsync(
      Office.EventType.SelectedItemsChanged,
      (): void => void this.load(),
      (result: Office.AsyncResult<void>): void => {
        if (result.status === Office.AsyncResultStatus.Failed) {
          console.error('Listening for the selected mails failed:', result.error);
        }
      }
    );
  }

  /**
   * Whether this client and deployment can file several mails at once: Graph
   * through nested app authentication, and a deployment configured for it.
   */
  public isBatchSupported(): boolean {
    const requirements = Office.context.requirements;

    return (
      requirements.isSetSupported('Mailbox', '1.13') &&
      requirements.isSetSupported('NestedAppAuth', '1.1') &&
      this._configService.config.graph !== undefined
    );
  }

  /**
   * Reads the selection off Outlook. Never rejects: a selection that cannot be
   * read counts as none, and the pane goes by the open mail.
   */
  public load(): Promise<Selection> {
    this._reading = this._reading.then(async () => {
      const selection = await this._read();
      this._selection.set(selection);
      return selection;
    });

    return this._reading;
  }

  private _canReadSelection(): boolean {
    return (
      typeof Office.context.mailbox.getSelectedItemsAsync === 'function' &&
      typeof Office.context.mailbox.addHandlerAsync === 'function'
    );
  }

  private async _read(): Promise<Selection> {
    if (!this._canReadSelection()) {
      return NO_SELECTION;
    }

    try {
      const items = await fromOffice<Office.SelectedItemDetails[]>((callback): void =>
        Office.context.mailbox.getSelectedItemsAsync(callback)
      );
      const mails = items.map(({ itemId, subject }) => ({ itemId, subject }));

      // Outlook only selects several mails within one mailbox, so the first
      // one tells for all of them. Only asked where the batch could be filed
      // otherwise: a single mail goes the single filing way, which serves
      // shared mailboxes too, and any other selection gets a message anyway.
      const batchFileable =
        mails.length > 1 && mails.length <= MAX_BATCH_SIZE && this.isBatchSupported();

      return {
        mails,
        sharedMailbox: batchFileable && (await this._isInSharedMailbox(mails[0].itemId))
      };
    } catch (error) {
      console.warn('Reading the selected mails failed:', error);
      return NO_SELECTION;
    }
  }

  /**
   * Only a loaded item tells which mailbox it is in, and loading one needs
   * requirement set 1.15. Below it, as on Outlook for Mac, a shared mailbox
   * cannot be told apart here, and neither can it where the check fails.
   */
  private async _isInSharedMailbox(itemId: string): Promise<boolean> {
    if (!Office.context.requirements.isSetSupported('Mailbox', '1.15')) {
      return false;
    }

    try {
      return await this._loadAndCheckOwner(itemId);
    } catch (error) {
      console.warn('Checking the mailbox of the selected mails failed:', error);
      return false;
    }
  }

  private async _loadAndCheckOwner(itemId: string): Promise<boolean> {
    const mailbox = Office.context.mailbox;
    // A draft in the selection loads in compose mode.
    const item = await fromOffice<Office.LoadedMessageRead | Office.LoadedMessageCompose>(
      (callback): void => mailbox.loadItemByIdAsync(itemId, callback)
    );

    try {
      // Items in the user's own mailbox have no shared properties at all.
      if (typeof item.getSharedPropertiesAsync !== 'function') {
        return false;
      }

      const { owner } = await fromOffice<Office.SharedProperties>((callback): void =>
        item.getSharedPropertiesAsync(callback)
      );

      return owner.toLowerCase() !== mailbox.userProfile.emailAddress.toLowerCase();
    } finally {
      // Outlook holds one loaded item at a time. A failed unload must not
      // override what the check found.
      try {
        await fromOffice<void>((callback): void => item.unloadAsync(callback));
      } catch (error) {
        console.warn('Unloading the checked mail failed:', error);
      }
    }
  }
}
